import { NavLink, useLocation } from 'react-router';
import { useLayoutEffect, useRef } from 'react';
import { Icon } from '@/components/Icon';
import { useSession } from '@/stores/session';
import s from './ContextualSidebar.module.css';

type Entry = { label: string; to: string; icon: string; key: string };
type Group = { title: string; entries: Entry[] };

const group = (title: string, entries: Entry[]): Group => ({ title, entries });
const entry = (key: string, label: string, to: string, icon: string): Entry => ({ key, label, to, icon });

const SHIPMENTS = group('发货视图', [
  entry('ready', '待发操作台', '/shipments', 'truck'),
  entry('today', '今日到期', '/shipments?view=today', 'alert'),
  entry('shortage', '缺货挂起', '/orders?view=shortage', 'box'),
  entry('assembling', '等待组装', '/orders?view=assembling', 'set'),
  entry('ledger', '发货批次台账', '/shipments?view=ledger', 'book'),
  entry('exceptions', '批次含异常', '/shipments?view=exceptions', 'alert'),
  entry('merge', '按客户合单', '/shipments?view=merge', 'team'),
  entry('warehouse', '按仓库', '/shipments?view=warehouse', 'bank'),
]);

const ACCOUNT_PERMISSIONS = group('账号权限', [
  entry('roles', '角色', '/users?tab=roles', 'set'), entry('accounts', '账号', '/users?tab=accounts', 'team'),
  entry('history', '权限变更记录', '/users/history', 'hist'), entry('migration', '迁移对照', '/users/migration-preview', 'alert'),
  entry('compare', '角色对比', '/users/compare', 'chart'),
]);
const SETTINGS = group('系统设置', [
  entry('basic', '基本设置', '/settings', 'set'), entry('assembly', '组装线与工人', '/settings/assembly', 'merge'),
  entry('carriers', '物流公司', '/settings/carriers', 'truck'), entry('shops', '店铺名称', '/settings/shops', 'store'),
  entry('warehouses', '仓库', '/settings/warehouses', 'home'), entry('users', '账号权限', '/users', 'team'),
  entry('audit', '操作日志', '/audit-logs', 'hist'), entry('imports', '导入导出历史', '/imports', 'up'),
]);
const ORDERS = group('订单视图', [
  entry('all', '全部订单', '/orders?view=all', 'cart'), entry('tasks', '我的待办', '/orders?view=tasks', 'clip'),
  entry('review', '等我审核', '/orders?view=review', 'alert'),
  entry('shortage', '缺货订单', '/orders?view=shortage', 'box'), entry('assembling', '等待组装', '/orders?view=assembling', 'set'),
  entry('work', '仓库作业', '/work', 'truck'),
]);
const INVENTORY = group('库存视图', [
  entry('all', '库存总览', '/inventory', 'box'), entry('warehouse', '按仓库明细', '/inventory/warehouses', 'bank'),
  entry('change', '变动汇总', '/inventory/changes', 'chart'), entry('shortage', '负可用', '/inventory/shortage', 'alert'),
  entry('transit', '在途待入库', '/inventory/transit', 'truck'), entry('noStock', '零库存', '/inventory?view=noStock', 'flag'),
  entry('tx', '出入库流水', '/inventory/transactions', 'book'),
]);
const PURCHASE = group('采购视图', [
  entry('suggestions', '采购建议', '/purchase-suggestions', 'chart'), entry('orders', '采购单', '/purchase-orders', 'inbox'),
  entry('inventory', '库存总览', '/inventory', 'box'), entry('transit', '在途待入库', '/inventory/transit', 'truck'),
]);

const GROUPS: { test: (path: string) => boolean; group: Group }[] = [
  { test: (p) => p === '/' || p === '/apps', group: group('业务导航', [
    entry('orders', '订单', '/orders', 'cart'), entry('shipments', '发货', '/shipments', 'truck'),
    entry('exceptions', '异常售后', '/exceptions', 'alert'), entry('purchase', '采购', '/purchase-suggestions', 'inbox'),
    entry('inventory', '库存', '/inventory', 'box'), entry('settlements', '往来结算', '/settlements', 'bank'),
  ]) },
  { test: (p) => p === '/work' || p.startsWith('/work/'), group: group('仓库作业', [
    entry('work', '作业台', '/work', 'truck'), entry('counts', '盘点任务', '/work/counts', 'box'),
    entry('inventory', '库存总览', '/inventory', 'chart'), entry('shortage', '负可用', '/inventory/shortage', 'alert'),
  ]) },
  { test: (p) => p === '/orders' || p.startsWith('/orders/'), group: ORDERS },
  { test: (p) => p === '/inventory' || p.startsWith('/inventory/'), group: INVENTORY },
  { test: (p) => p === '/purchase-suggestions' || p === '/purchase-orders' || p.startsWith('/purchase-orders/'), group: PURCHASE },
  { test: (p) => p.startsWith('/shipments/'), group: SHIPMENTS },
  { test: (p) => p === '/exceptions' || p.startsWith('/exceptions/'), group: group('异常视图', [
    entry('open', '异常记录', '/exceptions', 'alert'), entry('responsibility', '责任与损失', '/exceptions/responsibility', 'chart'),
  ]) },
  { test: (p) => p === '/products' || p.startsWith('/products/') || p === '/bundles' || p.startsWith('/bundles/') || p === '/gallery', group: group('产品资料', [
    entry('products', '产品管理', '/products', 'book'), entry('bundles', '产品套装', '/bundles', 'set'), entry('gallery', '产品画册', '/gallery', 'image'),
  ]) },
  { test: (p) => p === '/customers' || p.startsWith('/customers/'), group: group('客户资料', [
    entry('customers', '客户列表', '/customers', 'team'), entry('customer-settlement', '客户结算', '/settlements/customers', 'bank'),
    entry('orders', '订单', '/orders', 'cart'),
  ]) },
  { test: (p) => p === '/suppliers' || p.startsWith('/suppliers/'), group: group('供应商资料', [
    entry('suppliers', '供应商列表', '/suppliers', 'team'), entry('supplier-settlement', '供应商结算', '/settlements/suppliers', 'bank'),
    entry('purchase', '采购单', '/purchase-orders', 'inbox'),
  ]) },
  { test: (p) => p.startsWith('/settlements/'), group: group('结算视图', [
    entry('overview', '结算总览', '/settlements/overview', 'chart'), entry('customers', '客户结算', '/settlements/customers', 'team'),
    entry('suppliers', '供应商结算', '/settlements/suppliers', 'inbox'), entry('statements', '对账单', '/settlements/statements', 'book'),
  ]) },
  { test: (p) => p === '/settlements', group: group('结算视图', [
    entry('overview', '结算总览', '/settlements/overview', 'chart'), entry('customers', '客户结算', '/settlements/customers', 'team'),
    entry('suppliers', '供应商结算', '/settlements/suppliers', 'inbox'), entry('statements', '对账单', '/settlements/statements', 'book'),
  ]) },
  { test: (p) => p === '/reports', group: group('经营报表', [
    entry('reports', '经营总览', '/reports', 'chart'), entry('orders', '订单', '/orders', 'cart'),
    entry('inventory', '库存', '/inventory', 'box'), entry('settlements', '往来结算', '/settlements', 'bank'),
  ]) },
  { test: (p) => p.startsWith('/settings') || p.startsWith('/audit-logs') || p.startsWith('/imports'), group: SETTINGS },
  { test: (p) => p.startsWith('/users'), group: ACCOUNT_PERMISSIONS },
  { test: (p) => p.startsWith('/taobao'), group: group('淘宝业务', [
    entry('orders', '淘宝订单', '/taobao', 'cart'), entry('returns', '退货处理', '/taobao?view=returns', 'return'),
  ]) },
  { test: (p) => p === '/me' || p.startsWith('/me/'), group: group('我的账号', [
    entry('profile', '账号资料', '/me', 'user'), entry('appearance', '外观设置', '/me/appearance', 'palette'),
    entry('password', '修改密码', '/me/password', 'lock'), entry('notifications', '通知设置', '/me/notifications', 'bell'),
    entry('about', '关于', '/me/about', 'info'),
  ]) },
  { test: (p) => p === '/notifications', group: group('通知中心', [
    entry('notifications', '全部通知', '/notifications', 'bell'), entry('orders', '订单', '/orders', 'cart'),
    entry('exceptions', '异常售后', '/exceptions', 'alert'),
  ]) },
];

const ROUTES_WITH_OWN_SIDEBAR = new Set([
  '/orders', '/shipments', '/exceptions', '/inventory', '/inventory/warehouses', '/inventory/changes',
  '/inventory/transit', '/inventory/transactions', '/inventory/shortage', '/purchase-orders', '/purchase-suggestions',
  '/purchase-suggestions', '/products', '/bundles', '/gallery', '/settlements/customers',
  '/settlements/suppliers', '/settlements/statements',
]);

// 这些手机列表已经有自己的横向模块切换。详情/子列表仅有筛选按钮或状态筛选，仍显示模块导航。
const PHONE_ROUTES_WITH_OWN_NAV = new Set([
  '/orders', '/shipments', '/inventory', '/products', '/bundles', '/gallery',
  '/settlements/customers', '/settlements/suppliers', '/settlements/statements',
]);

const ADMIN_NATIVE_SIDEBAR = ['/settings', '/audit-logs', '/imports', '/users/accounts'];
const ROUTES_WITHOUT_CONTEXTUAL_NAV = new Set(['/', '/apps', '/reports']);

function getGroup(pathname: string) {
  return GROUPS.find((item) => item.test(pathname))?.group ?? group('业务导航', [
    entry('orders', '订单', '/orders', 'cart'), entry('shipments', '发货', '/shipments', 'truck'),
    entry('exceptions', '异常售后', '/exceptions', 'alert'), entry('purchase', '采购', '/purchase-suggestions', 'inbox'),
    entry('inventory', '库存', '/inventory', 'box'), entry('settlements', '往来结算', '/settlements', 'bank'),
  ]);
}

function activeEntry(pathname: string, search: string, items: Entry) {
  const target = new URL(items.to, window.location.origin);
  const params = new URLSearchParams(search);
  if (items.key === 'profile' && target.pathname === '/me') return pathname === '/me';
  if (items.key === 'basic' && target.pathname === '/settings') return pathname === '/settings';
  if (items.key === 'all' && target.pathname === '/inventory') return pathname === '/inventory' && !params.has('view');
  if (items.key === 'tasks' && target.pathname === '/orders') return pathname === '/orders' && (!params.has('view') || params.get('view') === 'tasks');
  const onTargetPath = pathname === target.pathname || pathname.startsWith(`${target.pathname}/`);
  if (!onTargetPath) return false;
  const targetParams = target.searchParams;
  for (const [key, value] of targetParams) if (params.get(key) !== value) return false;
  if ((items.key === 'ready' || items.key === 'all' || items.key === 'products' || items.key === 'bundles' || items.key === 'orders') && !targetParams.has('view')) return !params.has('view');
  return true;
}

export function ContextualSidebar({ formFactor }: { formFactor: 'desktop' | 'tablet' | 'phone' }) {
  const { pathname, search } = useLocation();
  const phoneNavRef = useRef<HTMLElement>(null);
  const can = useSession((state) => state.can);
  const adminNative = formFactor !== 'phone' && (ADMIN_NATIVE_SIDEBAR.some((p) => pathname === p || pathname.startsWith(`${p}/`)) || pathname === '/users');
  const adminPhoneTabs = formFactor === 'phone' && (pathname === '/users' || pathname === '/users/history' || pathname === '/users/migration-preview');
  const pageOwnsSidebar = formFactor === 'desktop'
    ? ROUTES_WITH_OWN_SIDEBAR.has(pathname)
    : formFactor === 'phone' && PHONE_ROUTES_WITH_OWN_NAV.has(pathname);
  // iPad 订单工作台已把订单列表作为二级侧栏；再插入模块导航会把详情挤窄。
  const tabletPageOwnsSidebar = formFactor === 'tablet' && (pathname === '/shipments' || pathname === '/orders');
  const base = getGroup(pathname);
  const entries = base.title === '订单视图'
    ? base.entries.filter((item) => item.key !== 'review' || can('orders.approve'))
    : base.entries;
  const current = {
    ...base,
    entries: (pathname === '/inventory' || pathname.startsWith('/inventory/')) && can('imports.read')
      ? [...entries, entry('imports', '导入历史', '/imports', 'down')]
      : entries,
  };
  useLayoutEffect(() => {
    if (formFactor !== 'phone') return;
    const nav = phoneNavRef.current;
    const active = nav?.querySelector<HTMLElement>(`.${s.phoneOn}`);
    if (!nav || !active) return;
    const reveal = () => {
      const navRect = nav.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const visibleLeft = navRect.left + nav.clientLeft + 12;
      const visibleRight = navRect.right - 12;
      let nextLeft = nav.scrollLeft;
      if (activeRect.left < visibleLeft) nextLeft += activeRect.left - visibleLeft;
      else if (activeRect.right > visibleRight) nextLeft += activeRect.right - visibleRight;
      if (nextLeft !== nav.scrollLeft) nav.scrollLeft = Math.max(0, nextLeft);
    };
    reveal();
    const frame = requestAnimationFrame(reveal);
    return () => cancelAnimationFrame(frame);
  }, [formFactor, pathname, search]);

  if (ROUTES_WITHOUT_CONTEXTUAL_NAV.has(pathname) || pageOwnsSidebar || tabletPageOwnsSidebar || adminNative || adminPhoneTabs) return null;

  if (formFactor === 'phone') return (
    <nav ref={phoneNavRef} className={s.phone} aria-label={current.title}>
      {current.entries.map((item) => {
        const active = activeEntry(pathname, search, item);
        return <NavLink key={item.key} to={item.to} className={({ isActive }) => `${s.phoneItem} ${isActive && active ? s.phoneOn : ''}`} aria-current={active ? 'page' : 'false'}>
        <Icon name={item.icon} size={14} /><span>{item.label}</span>
      </NavLink>;
      })}
    </nav>
  );

  const tabletCompact = formFactor === 'tablet' && (
    /^\/orders\/[^/]+\/stock$/.test(pathname)
    || pathname === '/products' || pathname.startsWith('/products/')
    || pathname === '/bundles' || pathname.startsWith('/bundles/') || pathname === '/gallery'
  );
  return <nav className={`${s.side} ${tabletCompact ? s.stockCompact : ''}`} aria-label={current.title}>
    <div className={s.title}>{current.title}</div>
    <div className={s.list}>{current.entries.map((item) => {
      const active = activeEntry(pathname, search, item);
      return <NavLink key={item.key} to={item.to} className={`${s.item} ${active ? s.on : ''}`} aria-current={active ? 'page' : 'false'}>
        <Icon name={item.icon} size={14} /><span>{item.label}</span>
      </NavLink>;
    })}</div>
  </nav>;
}
